// Text catalog for the Projektøkonomi / Project economics service page.
// Icons and images live in src/pages/ProjektoekonomiPage.tsx and are aligned by
// index with the card arrays here.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type ValueProp = { title: string; text: string };
type Phase = { label: string; title: string; items: string[] };
type Faq = { q: string; a: string };

type ProjektoekonomiStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  twTitle: string;
  twDescription: string;
  hero: { eyebrow: string; title: string; body: string; ctaPrimary: string; ctaSecondary: string };
  intro: { eyebrow: string; heading: string; p1: string; p2: string };
  why: { eyebrow: string; heading: string; props: ValueProp[] };
  approach: { eyebrow: string; heading: string; intro: string; phases: Phase[] };
  sectors: { eyebrow: string; heading: string; intro: string; items: string[] };
  faq: { eyebrow: string; heading: string; items: Faq[] };
  cta: { heading: string; body: string; button: string };
  jsonLd: { serviceType: string; name: string; description: string; audienceType: string };
};

export const projektoekonomiStrings: Record<Locale, ProjektoekonomiStrings> = {
  da: {
    meta: {
      title: "Projektøkonomi & controlling i byggeriet | MI Support",
      description:
        "Erfarne projektcontrollere fra MI Support styrer økonomien på store byggeprojekter. Budget, prognoser, cost control og rapportering.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg",
    },
    keywords:
      "projektøkonomi, projektcontroller, cost control, byggeøkonomi, projektregnskab, forecast byggeri, budget byggeri, økonomistyring projekt, earned value, ekstrakrav",
    ogTitle: "Projektøkonomi & projektcontrolling | MI Support",
    ogDescription:
      "Projektcontrollere der styrer økonomien på komplekse byggerier, budget, forecast, cost control og rapportering med fuld transparens.",
    twTitle: "Projektøkonomi & projektcontrolling | MI Support",
    twDescription: "Projektcontrollere der styrer økonomien på komplekse byggerier.",
    hero: {
      eyebrow: "Vores ydelser",
      title: "Projektøkonomi til jeres næste byggeprojekt",
      body: "MI Support stiller erfarne projektcontrollere til rådighed, der tager hånd om økonomien på jeres byggeprojekt fra første estimat. Vi sikrer fuld transparens, løbende prognoser og styr på ekstrakrav, så bygherre og ledelse kan træffe beslutninger på et solidt grundlag.",
      ctaPrimary: "Tag en uforpligtende snak",
      ctaSecondary: "Se alle ydelser",
    },
    intro: {
      eyebrow: "Controllere med byggeforståelse",
      heading: "Vi holder styr på økonomien ",
      p1: "På store byggeprojekter er økonomien oftest kompleks. Mange kontrakter, change orders, leverandørfakturaer og interne posteringer skal holdes op mod budget, prognose og kontrakt. Det kræver en dedikeret projektcontroller, der både forstår byggeri og økonomi.",
      p2: "MI Supports controllere indgår i jeres projektorganisation som bygherres forlængede arm. De arbejder tæt sammen med projektleder, byggeleder og bygherrerådgiver og rapporterer ind i jeres egne ERP- og rapporteringssystemer som SAP, Microsoft Dynamics, Unit4 eller Power BI.",
    },
    why: {
      eyebrow: "Hvorfor MI Support",
      heading: "Økonomi I kan styre projektet efter",
      props: [
        {
          title: "Uafhængig økonomisk kontrol",
          text: "Vores controllere er uafhængige af entreprenører og leverandører. Vi efterprøver fakturaer, ekstrakrav og afregninger, så bygherre kun betaler for det, der reelt er aftalt og leveret.",
        },
        {
          title: "Transparente prognoser",
          text: "Med løbende forecast, earned value og milestonetrend har bygherre og ledelsen en bedre mulighed for at se, hvor projektet lander økonomisk, og hvilke tiltag der kan justere kursen.",
        },
        {
          title: "Erfaring med store budgetter",
          text: "Vores projektcontrollere har styret budgetter i større industri-, life science- og infrastrukturbyggerier og forstår bygherrers krav til governance og dokumentation.",
        },
      ],
    },
    approach: {
      eyebrow: "Vores tilgang",
      heading: "Projektøkonomi gennem hele projektets levetid",
      intro:
        "Vi etablerer en gennemsigtig økonomimodel fra starten, så bygherre og styregruppe altid har fuldt overblik over budget, forbrug, forpligtelser og forventet slutpris. Det giver jer tryghed, færre overraskelser og et solidt grundlag for at træffe de rigtige beslutninger, uanset hvor komplekst projektet er.",
      phases: [
        {
          label: "Fase 01",
          title: "Budget & estimater",
          items: [
            "Estimater i tidlige faser og investeringscase",
            "Strukturering af projektbudget",
            "Cost Breakdown Structure (CBS) og kontoplan",
            "Risikobudget og reserver",
          ],
        },
        {
          label: "Fase 02",
          title: "Cost control & forecast",
          items: [
            "Forpligtelses- og forbrugsstyring",
            "Forecast, prognoser og earned value",
            "Change orders, variations og ekstrakrav",
            "Cashflow-styring og likviditetsprognoser",
          ],
        },
        {
          label: "Fase 03",
          title: "Rapportering & afslutning",
          items: [
            "Månedsrapportering til bygherre, ledelse og styregruppe",
            "Sluteafregning og granskning af entreprenørafregning",
            "Slutregnskab og overlevering til drift",
            "Lessons learned og benchmarking",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Brancher",
      heading: "Erfaring med økonomistyring af krævende byggerier",
      intro:
        "Vores projektcontrollere har stået i spidsen for økonomien på nogle af Danmarks mest komplekse byggeprojekter, hvor governance, dokumentation og revisionsspor er lige så vigtigt som tallene i bunden.",
      items: [
        "Life science & pharma (GMP)",
        "Industri- og produktionsanlæg",
        "Logistik og datacentre",
        "Kontor- og hovedsædebyggeri",
        "Offentlige bygherrer",
        "Infrastruktur og forsyning",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Ofte stillede spørgsmål om projektøkonomi",
      items: [
        {
          q: "Hvad laver en projektcontroller på et byggeprojekt?",
          a: "En projektcontroller har det fulde overblik over projektets økonomi: budget, forpligtelser, forbrug, forecast, change orders og cashflow. Controlleren rapporterer løbende til bygherre, projektleder og styregruppe og sikrer, at økonomien følger projektets fremdrift.",
        },
        {
          q: "Hvad er forskellen på projektøkonomi og almindelig bogføring?",
          a: "Bogføringen registrerer historiske transaktioner. Projektøkonomi er fremadrettet styring i form af budget, forecast, prognoser og scenarier kombineret med en dyb forståelse for kontrakter, entrepriseformer og byggeprocesser. MI Supports controllere kobler de to verdener sammen.",
        },
        {
          q: "Arbejder I i vores egne systemer?",
          a: "Ja. Vores controllere arbejder typisk i bygherrens egne ERP- og rapporteringssystemer, fx SAP, Microsoft Dynamics, Unit4, Navision eller dedikerede cost control-værktøjer, og rapporterer i Power BI eller Excel-modeller i henhold til jeres standarder.",
        },
        {
          q: "Kan I tage hånd om ekstrakrav og granskning af entreprenørafregning?",
          a: "Ja. Vi gennemgår change orders og ekstrakrav i forhold til kontrakt og fagligt grundlag, forhandler med entreprenørerne og dokumenterer beslutningerne. Det giver bygherre tryghed og et stærkt grundlag, hvis sager skulle udvikle sig til tvister.",
        },
      ],
    },
    cta: {
      heading: "Skal vi tage hånd om jeres projektøkonomi?",
      body: "Står I overfor et stort byggeprojekt, eller har jeres nuværende projekt brug for bedre økonomistyring? Vores controllere er klar til en uforpligtende snak om jeres situation og behov.",
      button: "Kontakt os og hør mere",
    },
    jsonLd: {
      serviceType: "Projektøkonomi og projektcontrolling",
      name: "Projektøkonomi",
      description:
        "Professionel projektøkonomi og cost control til store og komplekse byggeprojekter, budget, forecast, prognoser, ekstrakrav og rapportering.",
      audienceType: "Bygherrer, industri, life science, offentlige bygherrer",
    },
  },

  en: {
    meta: {
      title: "Project economics & controlling | MI Support",
      description:
        "Experienced project controllers from MI Support manage the economics of large construction projects. Budget, forecasts, cost control and reporting.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg",
    },
    keywords:
      "project economics, project controller, cost control, construction economics, project accounting, construction forecast, construction budget, project financial control, earned value, additional claims",
    ogTitle: "Project economics & project controlling | MI Support",
    ogDescription:
      "Project controllers who manage the economics of complex builds, budget, forecast, cost control and reporting with full transparency.",
    twTitle: "Project economics & project controlling | MI Support",
    twDescription: "Project controllers who manage the economics of complex builds.",
    hero: {
      eyebrow: "Our services",
      title: "Project economics for your next construction project",
      body: "MI Support provides experienced project controllers who take care of the economics of your construction project from the very first estimate. We ensure full transparency, ongoing projections and control of additional claims, so the client and management can make decisions on a solid basis.",
      ctaPrimary: "Have a no-obligation chat",
      ctaSecondary: "See all services",
    },
    intro: {
      eyebrow: "Controllers with construction insight",
      heading: "We keep the economics under control",
      p1: "On large construction projects the economics are usually complex. Many contracts, change orders, supplier invoices and internal postings must be held up against budget, forecast and contract. This requires a dedicated project controller who understands both construction and economics.",
      p2: "MI Support's controllers join your project organisation as the client's extended arm. They work closely with the project manager, construction manager and client advisor, and report into your own ERP and reporting systems such as SAP, Microsoft Dynamics, Unit4 or Power BI.",
    },
    why: {
      eyebrow: "Why MI Support",
      heading: "Economics you can steer the project by",
      props: [
        {
          title: "Independent financial control",
          text: "Our controllers are independent of contractors and suppliers. We verify invoices, additional claims and settlements, so the client only pays for what has actually been agreed and delivered.",
        },
        {
          title: "Transparent projections",
          text: "With ongoing forecast, earned value and milestone trend, the client and management have a better opportunity to see where the project will land financially and which measures can adjust the course.",
        },
        {
          title: "Experience with large budgets",
          text: "Our project controllers have managed budgets in larger industrial, life science and infrastructure builds, and understand clients' requirements for governance and documentation.",
        },
      ],
    },
    approach: {
      eyebrow: "Our approach",
      heading: "Project economics throughout the project's life cycle",
      intro:
        "We establish a transparent economic model from the start, so the client and steering committee always have a full overview of budget, spend, commitments and expected final price. This gives you peace of mind, fewer surprises and a solid basis for making the right decisions, no matter how complex the project is.",
      phases: [
        {
          label: "Phase 01",
          title: "Budget & estimates",
          items: [
            "Estimates in early phases and investment case",
            "Structuring of the project budget",
            "Cost Breakdown Structure (CBS) and chart of accounts",
            "Risk budget and reserves",
          ],
        },
        {
          label: "Phase 02",
          title: "Cost control & forecast",
          items: [
            "Commitment and spend management",
            "Forecast, projections and earned value",
            "Change orders, variations and additional claims",
            "Cashflow management and liquidity projections",
          ],
        },
        {
          label: "Phase 03",
          title: "Reporting & closeout",
          items: [
            "Monthly reporting to the client, management and steering committee",
            "Final settlement and review of contractor settlement",
            "Final accounts and handover to operations",
            "Lessons learned and benchmarking",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Sectors",
      heading: "Experience with financial control of demanding builds",
      intro:
        "Our project controllers have led the economics on some of Denmark's most complex construction projects, where governance, documentation and audit trail are just as important as the numbers at the bottom.",
      items: [
        "Life science & pharma (GMP)",
        "Industrial and production facilities",
        "Logistics and data centres",
        "Office and headquarters construction",
        "Public clients",
        "Infrastructure and utilities",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Frequently asked questions about project economics",
      items: [
        {
          q: "What does a project controller do on a construction project?",
          a: "A project controller has the full overview of the project's economics: budget, commitments, spend, forecast, change orders and cashflow. The controller reports regularly to the client, project manager and steering committee and ensures that the economics follow the project's progress.",
        },
        {
          q: "What is the difference between project economics and ordinary bookkeeping?",
          a: "Bookkeeping records historical transactions. Project economics is forward-looking control in the form of budget, forecast, projections and scenarios combined with a deep understanding of contracts, contracting forms and construction processes. MI Support's controllers connect the two worlds.",
        },
        {
          q: "Do you work in our own systems?",
          a: "Yes. Our controllers typically work in the client's own ERP and reporting systems, e.g. SAP, Microsoft Dynamics, Unit4, Navision or dedicated cost control tools, and report in Power BI or Excel models in accordance with your standards.",
        },
        {
          q: "Can you handle additional claims and review of contractor settlement?",
          a: "Yes. We review change orders and additional claims against contract and professional basis, negotiate with the contractors and document the decisions. This gives the client peace of mind and a strong basis should matters develop into disputes.",
        },
      ],
    },
    cta: {
      heading: "Shall we take care of your project economics?",
      body: "Are you facing a large construction project, or does your current project need better financial control? Our controllers are ready for a no-obligation chat about your situation and needs.",
      button: "Contact us to learn more",
    },
    jsonLd: {
      serviceType: "Project economics and project controlling",
      name: "Project economics",
      description:
        "Professional project economics and cost control for large and complex construction projects, budget, forecast, projections, additional claims and reporting.",
      audienceType: "Clients, industry, life science, public clients",
    },
  },

  de: {
    meta: {
      title: "Projektökonomie & Controlling | MI Support",
      description:
        "Erfahrene Projektcontroller von MI Support steuern die Wirtschaftlichkeit großer Bauprojekte. Budget, Prognosen, Cost Control und Berichtswesen.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg",
    },
    keywords:
      "Projektökonomie, Projektcontroller, Cost Control, Bauökonomie, Projektabrechnung, Bau-Forecast, Baubudget, Projektkostensteuerung, earned value, Nachträge",
    ogTitle: "Projektökonomie & Projektcontrolling | MI Support",
    ogDescription:
      "Projektcontroller, die die Wirtschaftlichkeit komplexer Bauten steuern, Budget, Forecast, Cost Control und Berichtswesen mit voller Transparenz.",
    twTitle: "Projektökonomie & Projektcontrolling | MI Support",
    twDescription: "Projektcontroller, die die Wirtschaftlichkeit komplexer Bauten steuern.",
    hero: {
      eyebrow: "Unsere Leistungen",
      title: "Projektökonomie für Ihr nächstes Bauprojekt",
      body: "MI Support stellt erfahrene Projektcontroller zur Verfügung, die sich um die Wirtschaftlichkeit Ihres Bauprojekts ab dem ersten Schätzwert kümmern. Wir sorgen für volle Transparenz, laufende Prognosen und Kontrolle über Nachträge, sodass Bauherr und Leitung Entscheidungen auf einer soliden Grundlage treffen können.",
      ctaPrimary: "Unverbindliches Gespräch führen",
      ctaSecondary: "Alle Leistungen ansehen",
    },
    intro: {
      eyebrow: "Controller mit Bauverständnis",
      heading: "Wir behalten die Wirtschaftlichkeit im Griff",
      p1: "Bei großen Bauprojekten ist die Wirtschaftlichkeit meist komplex. Viele Verträge, Change Orders, Lieferantenrechnungen und interne Buchungen müssen gegen Budget, Prognose und Vertrag gehalten werden. Das erfordert einen dedizierten Projektcontroller, der sowohl Bau als auch Wirtschaftlichkeit versteht.",
      p2: "Die Controller von MI Support sind als verlängerter Arm des Bauherrn Teil Ihrer Projektorganisation. Sie arbeiten eng mit Projektleiter, Bauleiter und Bauherrenberater zusammen und berichten in Ihre eigenen ERP- und Berichtssysteme wie SAP, Microsoft Dynamics, Unit4 oder Power BI.",
    },
    why: {
      eyebrow: "Warum MI Support",
      heading: "Wirtschaftlichkeit, nach der Sie das Projekt steuern können",
      props: [
        {
          title: "Unabhängige wirtschaftliche Kontrolle",
          text: "Unsere Controller sind unabhängig von Auftragnehmern und Lieferanten. Wir prüfen Rechnungen, Nachträge und Abrechnungen, sodass der Bauherr nur für das bezahlt, was tatsächlich vereinbart und geliefert wurde.",
        },
        {
          title: "Transparente Prognosen",
          text: "Mit laufendem Forecast, Earned Value und Meilenstein-Trend haben Bauherr und Leitung eine bessere Möglichkeit zu sehen, wo das Projekt wirtschaftlich landet und welche Maßnahmen den Kurs anpassen können.",
        },
        {
          title: "Erfahrung mit großen Budgets",
          text: "Unsere Projektcontroller haben Budgets in größeren Industrie-, Life-Science- und Infrastrukturbauten gesteuert und verstehen die Anforderungen der Bauherren an Governance und Dokumentation.",
        },
      ],
    },
    approach: {
      eyebrow: "Unser Ansatz",
      heading: "Projektökonomie über den gesamten Lebenszyklus des Projekts",
      intro:
        "Wir etablieren von Anfang an ein transparentes Wirtschaftlichkeitsmodell, sodass Bauherr und Lenkungsausschuss stets einen vollständigen Überblick über Budget, Verbrauch, Verpflichtungen und erwarteten Endpreis haben. Das gibt Ihnen Sicherheit, weniger Überraschungen und eine solide Grundlage, um die richtigen Entscheidungen zu treffen, egal wie komplex das Projekt ist.",
      phases: [
        {
          label: "Phase 01",
          title: "Budget & Schätzungen",
          items: [
            "Schätzungen in frühen Phasen und Investitionscase",
            "Strukturierung des Projektbudgets",
            "Cost Breakdown Structure (CBS) und Kontenplan",
            "Risikobudget und Reserven",
          ],
        },
        {
          label: "Phase 02",
          title: "Cost Control & Forecast",
          items: [
            "Verpflichtungs- und Verbrauchssteuerung",
            "Forecast, Prognosen und Earned Value",
            "Change Orders, Variations und Nachträge",
            "Cashflow-Steuerung und Liquiditätsprognosen",
          ],
        },
        {
          label: "Phase 03",
          title: "Berichtswesen & Abschluss",
          items: [
            "Monatsberichte an Bauherr, Leitung und Lenkungsausschuss",
            "Schlussabrechnung und Prüfung der Auftragnehmerabrechnung",
            "Schlussrechnung und Übergabe an den Betrieb",
            "Lessons Learned und Benchmarking",
          ],
        },
      ],
    },
    sectors: {
      eyebrow: "Branchen",
      heading: "Erfahrung mit der Kostensteuerung anspruchsvoller Bauten",
      intro:
        "Unsere Projektcontroller haben die Wirtschaftlichkeit einiger der komplexesten Bauprojekte Dänemarks geleitet, bei denen Governance, Dokumentation und Revisionsspur ebenso wichtig sind wie die Zahlen unter dem Strich.",
      items: [
        "Life Science & Pharma (GMP)",
        "Industrie- und Produktionsanlagen",
        "Logistik und Rechenzentren",
        "Büro- und Hauptsitzbauten",
        "Öffentliche Bauherren",
        "Infrastruktur und Versorgung",
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Häufig gestellte Fragen zur Projektökonomie",
      items: [
        {
          q: "Was macht ein Projektcontroller bei einem Bauprojekt?",
          a: "Ein Projektcontroller hat den vollen Überblick über die Wirtschaftlichkeit des Projekts: Budget, Verpflichtungen, Verbrauch, Forecast, Change Orders und Cashflow. Der Controller berichtet laufend an Bauherr, Projektleiter und Lenkungsausschuss und stellt sicher, dass die Wirtschaftlichkeit dem Fortschritt des Projekts folgt.",
        },
        {
          q: "Was ist der Unterschied zwischen Projektökonomie und gewöhnlicher Buchhaltung?",
          a: "Die Buchhaltung erfasst historische Transaktionen. Projektökonomie ist vorausschauende Steuerung in Form von Budget, Forecast, Prognosen und Szenarien, kombiniert mit einem tiefen Verständnis für Verträge, Vergabeformen und Bauprozesse. Die Controller von MI Support verbinden die beiden Welten.",
        },
        {
          q: "Arbeiten Sie in unseren eigenen Systemen?",
          a: "Ja. Unsere Controller arbeiten typischerweise in den eigenen ERP- und Berichtssystemen des Bauherrn, z. B. SAP, Microsoft Dynamics, Unit4, Navision oder dedizierten Cost-Control-Werkzeugen, und berichten in Power BI oder Excel-Modellen gemäß Ihren Standards.",
        },
        {
          q: "Können Sie sich um Nachträge und die Prüfung der Auftragnehmerabrechnung kümmern?",
          a: "Ja. Wir prüfen Change Orders und Nachträge im Verhältnis zu Vertrag und fachlicher Grundlage, verhandeln mit den Auftragnehmern und dokumentieren die Entscheidungen. Das gibt dem Bauherrn Sicherheit und eine starke Grundlage, falls sich Fälle zu Streitigkeiten entwickeln sollten.",
        },
      ],
    },
    cta: {
      heading: "Sollen wir uns um Ihre Projektökonomie kümmern?",
      body: "Stehen Sie vor einem großen Bauprojekt, oder benötigt Ihr aktuelles Projekt eine bessere Kostensteuerung? Unsere Controller sind bereit für ein unverbindliches Gespräch über Ihre Situation und Bedürfnisse.",
      button: "Kontaktieren Sie uns für mehr Informationen",
    },
    jsonLd: {
      serviceType: "Projektökonomie und Projektcontrolling",
      name: "Projektökonomie",
      description:
        "Professionelle Projektökonomie und Cost Control für große und komplexe Bauprojekte, Budget, Forecast, Prognosen, Nachträge und Berichtswesen.",
      audienceType: "Bauherren, Industrie, Life Science, öffentliche Bauherren",
    },
  },
};
